import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as authActions from '../../Redux/Auth/AuthActions';
import { InputComponent } from '../Inputs/input-component';
import { ButtonPrimary } from '../Buttons/button-component';
import { getLoginData } from '../../Public/login-data';
import useScreenSize from '../../Hooks/useScreenSize';
import { motion, AnimatePresence } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

/**
 * Reusable auth form — login, register, or forgot-password.
 * Embeddable anywhere (cart, modal, full-page).
 *
 * Props:
 *   mode: 'login' | 'register'
 *   onModeChange(mode): callback to switch mode from outside
 *   onSuccess(): optional callback after successful auth (for embedded use)
 *   compact: boolean — smaller padding/typography for inline embedding
 */
const AuthForm = ({ mode = 'login', onModeChange, onSuccess, compact = false, initialName = '', initialEmail = '' }) => {
    const dispatch = useDispatch();
    const { isSmallScreen } = useScreenSize();
    const { isAuthenticated } = useSelector((s) => s.auth);
    const { dir = 'ltr', language = 'en' } = useSelector((s) => s.systemData || {});
    const gsiRef   = useRef(null);
    const prevAuth = useRef(isAuthenticated); // init to current state so mount doesn't re-fire
    const txt = getLoginData(language);

    const [email, setEmail] = useState(initialEmail || '');
    const [password, setPassword] = useState('');
    const [name, setName] = useState(initialName || '');
    const [smsConsent,   setSmsConsent]   = useState(false);
    const [emailConsent, setEmailConsent] = useState(false);
    const [rememberMe,   setRememberMe]   = useState(true);

    // Forgot-password state (local — doesn't affect parent mode)
    const [forgotMode,    setForgotMode]    = useState(false);
    const [forgotSent,    setForgotSent]    = useState(false);
    const [forgotEmail,   setForgotEmail]   = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    // Pre-fill from parent when props arrive (only if user hasn't typed yet)
    useEffect(() => {
        if (initialName && !name) setName(initialName);
    }, [initialName]);

    useEffect(() => {
        if (initialEmail && !email) setEmail(initialEmail);
    }, [initialEmail]);

    // Detect the moment auth succeeds — call onSuccess once
    useEffect(() => {
        if (isAuthenticated && !prevAuth.current) {
            prevAuth.current = true;
            onSuccess?.();
        } else if (!isAuthenticated) {
            prevAuth.current = false;
        }
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // Google GSI button — shown in both login and register modes
    // Re-renders when mode or screen size changes so width stays correct
    useEffect(() => {
        try {
            if (window.google && gsiRef.current) {
                const gsiWidth = isSmallScreen || compact ? 310 : 400;
                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_INFRA_GOOGLE_CLIENT_ID,
                    callback: (res) => dispatch(authActions.handleLoginWithGoogle(res)),
                });
                window.google.accounts.id.renderButton(gsiRef.current, {
                    theme: 'outline',
                    size: 'large',
                    logo_alignment: 'center',
                    text: mode === 'register' ? 'signup_with' : 'signin_with',
                    width: gsiWidth,
                    shape: 'rectangular',
                });
            }
        } catch (e) { }
    }, [mode, dispatch, isSmallScreen]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (mode === 'register') {
            dispatch(authActions.registerUser(name, email, password, smsConsent, emailConsent));
        } else {
            dispatch(authActions.loginUnified(email, password, rememberMe));
        }
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        await dispatch(authActions.forgotPassword(forgotEmail));
        setForgotSent(true);
        setResendCooldown(60);
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        await dispatch(authActions.forgotPassword(forgotEmail));
        setResendCooldown(60);
    };

    const headingSize = compact ? 'text-2xl' : 'text-4xl lg:text-5xl';
    const isRegister = mode === 'register';

    // ── Forgot-password view ──────────────────────────────────────────────────────
    if (forgotMode) {
        return (
            <div className={`${compact ? 'max-w-md' : 'max-w-[400px]'} mx-auto`}>
                <AnimatePresence mode="wait">
                    {forgotSent ? (
                        <motion.div
                            key="sent"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.4, ease: EASE }}
                            className={dir === 'rtl' ? 'text-right' : 'text-left'}
                        >
                            {/* Envelope icon */}
                            <div className="w-14 h-14 rounded-2xl bg-secondary-50 border border-secondary-100 flex items-center justify-center mb-5">
                                <svg className="w-7 h-7 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>

                            <h2 className={`${headingSize} font-black text-primary-950 uppercase  italic leading-none mb-2`} style={{ fontFamily: 'Impact, sans-serif' }}>
                                {txt.forgotTitle}
                            </h2>
                            <p className="text-sm font-medium text-gray-400 mt-2">{txt.forgotSuccess}</p>

                            {forgotEmail && (
                                <p className="text-xs font-bold text-primary-950 mt-1 break-all">{forgotEmail}</p>
                            )}

                            {/* Resend */}
                            <div className="mt-6">
                                <p className="text-xs text-gray-400 font-medium mb-3">
                                    {txt.resendHint || "Didn't get the email?"}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={resendCooldown > 0}
                                    className="relative w-full py-3 rounded-2xl border-2 border-gray-200 overflow-hidden group disabled:cursor-not-allowed transition-colors hover:border-secondary-400"
                                >
                                    {/* Cooldown progress fill */}
                                    {resendCooldown > 0 && (
                                        <span
                                            className="absolute inset-0 bg-gray-100 origin-left transition-none"
                                            style={{ transform: `scaleX(${resendCooldown / 60})`, transformOrigin: 'left' }}
                                        />
                                    )}
                                    <span className="relative flex items-center justify-center gap-2">
                                        {resendCooldown > 0 ? (
                                            <>
                                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                                                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                                                </svg>
                                                <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                                                    {resendCooldown}s
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-3.5 h-3.5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                                </svg>
                                                <span className="text-xs font-black uppercase tracking-widest text-secondary-500 group-hover:text-secondary-600 transition-colors">
                                                    {txt.resendBtn || 'Resend Email'}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </button>
                            </div>

                            <div className="mt-5">
                                <button
                                    type="button"
                                    onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); setResendCooldown(0); }}
                                    className="text-secondary-500 font-bold text-sm hover:underline"
                                >
                                    ← {txt.backToLogin}
                                </button>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.4, ease: EASE }}
                        >
                            <div className={`mb-6 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                                <h2 className={`${headingSize} font-black text-primary-950 uppercase  italic leading-none mb-1`} style={{ fontFamily: 'Impact, sans-serif' }}>
                                    {txt.forgotTitle}
                                </h2>
                                <p className="text-sm font-medium text-gray-400 mt-2">{txt.forgotSubtitle}</p>
                            </div>
                            <form onSubmit={handleForgotSubmit} className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-gray-500 uppercase px-1">{txt.emailLabel}</label>
                                    <InputComponent
                                        dir="ltr" type="email" placeholder={txt.emailPlaceholder}
                                        value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                                        className="!text-left" size="lg" required
                                    />
                                </div>
                                <ButtonPrimary type="submit" fullWidth size="md">
                                    {txt.forgotBtn}
                                </ButtonPrimary>
                            </form>
                            <div className="mt-5 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setForgotMode(false)}
                                    className="text-secondary-500 font-bold text-sm hover:underline"
                                >
                                    ← {txt.backToLogin}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    // ── Login / Register view ─────────────────────────────────────────────────────
    return (
        <div className={` sm:max-w-[400px] max-w-[304px] mx-auto`}>
            <div className={`mb-6 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                <h2
                    className={`${headingSize} font-black text-primary-950 uppercase  italic leading-none mb-1`}
                    style={{ fontFamily: 'Impact, sans-serif' }}
                >
                    {isRegister ? txt.registerTitle : txt.title}
                </h2>
                <p className="text-sm font-medium text-gray-400 mt-2">
                    {isRegister ? txt.registerSubtitle : txt.subtitle}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-500 uppercase px-1">{txt.nameLabel}</label>
                        <InputComponent
                            dir="ltr" type="text" placeholder={txt.namePlaceholder}
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="!text-left" size="lg"
                        />
                    </div>
                )}

                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase px-1">{txt.emailLabel}</label>
                    <InputComponent
                        dir="ltr" type="email" placeholder={txt.emailPlaceholder}
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        className="!text-left" size="lg" required
                    />
                </div>

                <div className="space-y-1">
                    <div className="flex items-center justify-between px-1">
                        <label className="text-xs font-bold text-gray-500 uppercase">{txt.passwordLabel}</label>
                        {!isRegister && (
                            <button
                                type="button"
                                onClick={() => setForgotMode(true)}
                                className="text-[11px] font-bold text-secondary-500 hover:underline"
                            >
                                {txt.forgotPassword}
                            </button>
                        )}
                    </div>
                    <InputComponent
                        dir="ltr" type="password" placeholder={txt.passwordPlaceholder}
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="!text-left" size="lg" required
                    />
                </div>

                {isRegister && (
                    <div className="space-y-2 pt-1">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox" checked={smsConsent}
                                onChange={e => setSmsConsent(e.target.checked)}
                                className="mt-0.5 accent-secondary-500 w-4 h-4 shrink-0"
                            />
                            <span className="text-xs text-gray-500 font-medium leading-relaxed">{txt.smsConsentLabel}</span>
                        </label>
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox" checked={emailConsent}
                                onChange={e => setEmailConsent(e.target.checked)}
                                className="mt-0.5 accent-secondary-500 w-4 h-4 shrink-0"
                            />
                            <span className="text-xs text-gray-500 font-medium leading-relaxed">{txt.emailConsentLabel}</span>
                        </label>
                        <p className="text-[10px] text-gray-400 pl-6">{txt.consentNote}</p>
                    </div>
                )}

                {!isRegister && (
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={e => setRememberMe(e.target.checked)}
                            className="accent-secondary-500 w-4 h-4 shrink-0"
                        />
                        <span className="text-xs text-gray-500 font-medium">{txt.rememberMe}</span>
                    </label>
                )}

                <div className="space-y-1 ">
                    <ButtonPrimary type="submit" fullWidth size="sm">
                        {isRegister ? txt.registerBtn : txt.loginBtn}
                    </ButtonPrimary>
                </div>

                <div className="relative py-3">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                        <span className="bg-white px-2 text-gray-400 font-medium uppercase tracking-widest">
                            {txt.orDivider}
                        </span>
                    </div>
                </div>
                <div className="flex justify-center w-full">
                    <div ref={gsiRef} style={{ width: '100%', maxWidth: isSmallScreen || compact ? 304 : 400 }} />
                </div>
            </form>

            {onModeChange && (
                <div className="mt-5 flex justify-center items-center gap-2 text-sm">
                    <span className="text-gray-400 font-medium">
                        {isRegister ? txt.haveAccount : txt.noAccount}
                    </span>
                    <button
                        type="button"
                        onClick={() => onModeChange(isRegister ? 'login' : 'register')}
                        className="text-secondary-500 font-bold hover:underline"
                    >
                        {isRegister ? txt.signIn : txt.signUp}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AuthForm;
